/**
 * Signup Page
 * User registration with validation
 */
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../contexts/AuthContext';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    // Filled in when they came from the login page's "Create account"
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  // { message, code }
  const [error, setError] = useState(null);
  // Signed up, waiting for an admin to approve the account
  const [pending, setPending] = useState(false);

  // Password validation
  const passwordValidation = {
    minLength: formData.password.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.password),
    hasLowerCase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordValidation).every((v) => v);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // The server checks the email domain (it also allows the tool's admins)

    // Validate password
    if (!isPasswordValid) {
      setError({ message: 'Password does not meet requirements' });
      return;
    }

    // Validate password match
    if (!passwordsMatch) {
      setError({ message: 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const result = await signup(formData.email, formData.password, formData.fullName);
      if (result?.pending) setPending(true);
      else navigate('/dashboard');
    } catch (err) {
      setError({ message: err.message || 'Sign-up failed. Please try again.', code: err.code });
    } finally {
      setLoading(false);
    }
  };

  const ValidationItem = ({ valid, text }) => (
    <ListItem dense disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {valid ? (
          <CheckCircleIcon color="success" fontSize="small" />
        ) : (
          <CancelIcon color="disabled" fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          variant: 'body2',
          color: valid ? 'text.primary' : 'text.disabled',
        }}
      />
    </ListItem>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 4, borderRadius: 2 }}>
          {/* Logo/Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
              Create Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Join the MAS Holdings team
            </Typography>
          </Box>

          {!pending && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              If an admin has already added your email, you can start straight away after creating your
              account. Otherwise your request goes to the admins, and you can sign in once one approves it.
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              severity={error.code === 'pending' ? 'info' : 'error'}
              sx={{ mb: 3 }}
              action={error.code === 'exists' ? (
                <Button component={RouterLink} to="/login" color="inherit" size="small" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Sign in
                </Button>
              ) : null}
            >
              {error.message}
            </Alert>
          )}

          {pending ? (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                Your account has been created and is waiting for approval. Your request is now on the
                admins' Users page, and you'll be able to sign in as soon as an admin approves it.
              </Alert>
              <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth sx={{ py: 1.5 }}>
                Back to Sign In
              </Button>
            </Box>
          ) : (
          /* Signup Form */
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              margin="normal"
              autoComplete="name"
              autoFocus
            />

            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              placeholder="yourname@masholdings.com"
              helperText="Your @masholdings.com email. New accounts are approved by an admin."
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="new-password"
            />

            {/* Password Requirements */}
            {formData.password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Password requirements:
                </Typography>
                <List dense>
                  <ValidationItem
                    valid={passwordValidation.minLength}
                    text="At least 8 characters"
                  />
                  <ValidationItem
                    valid={passwordValidation.hasUpperCase}
                    text="One uppercase letter"
                  />
                  <ValidationItem
                    valid={passwordValidation.hasLowerCase}
                    text="One lowercase letter"
                  />
                  <ValidationItem
                    valid={passwordValidation.hasNumber}
                    text="One number"
                  />
                </List>
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="new-password"
              error={formData.confirmPassword && !passwordsMatch}
              helperText={
                formData.confirmPassword && !passwordsMatch
                  ? 'Passwords do not match'
                  : ''
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !isPasswordValid || !passwordsMatch}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>

            {/* Login Link */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link component={RouterLink} to="/login" underline="hover">
                  Sign In
                </Link>
              </Typography>
            </Box>
          </form>
          )}
        </Paper>

        {/* Footer */}
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 3, color: 'white', opacity: 0.9 }}
        >
          © 2026 MAS Holdings. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
